/**
 * ChatMessages.tsx
 *
 * Scroll container for the chat message list. Maps over the messages array and
 * routes each message to the appropriate subcomponent (UserMessage or
 * AssistantMessage) via the ChatMessage routing shim.
 *
 * Per Phase 1b §1.1 Q4, ScrollButtons are folded into this component — they
 * are imported and rendered here rather than being a sibling in App.tsx.
 *
 * DOM contract:
 *   - role="log" aria-label="Conversation" on the scroll container (I1 gate)
 *   - messagesEndRef forwarded for scroll anchor
 *   - onScroll forwarded for scroll button state management
 */

import { forwardRef } from "react";
import type { RefObject, MutableRefObject } from "react";
import type { ChatMessage as ChatMessageType, AuthUser } from "../../types/index.js";
import ChatMessage from "../ChatMessage.js";
import ScrollButtons from "../ScrollButtons.js";

export interface ChatMessagesProps {
	messages: ChatMessageType[];
	isStreaming: boolean;
	user?: AuthUser;
	onApproveAction?: (actionId: string) => void;
	onRejectAction?: (actionId: string) => void;
	/** Ref forwarded to the scroll container for chatMessagesRef logic in App.tsx. */
	scrollRef: RefObject<HTMLDivElement> | MutableRefObject<HTMLDivElement | null>;
	/** Ref for the invisible sentinel at the end of the list — used for auto-scroll. */
	endRef: RefObject<HTMLDivElement> | MutableRefObject<HTMLDivElement | null>;
	onScroll: () => void;
	/** Empty-state message shown when there are no messages. */
	emptyStateMessage?: string;
	/** Scroll button visibility — managed by App.tsx scroll handler. */
	showScrollTop: boolean;
	showScrollBottom: boolean;
	onScrollTop: () => void;
	onScrollBottom: () => void;
}

/**
 * Scroll container that renders the full message list.
 * Routes each message to ChatMessage (routing shim) which delegates to
 * UserMessage or AssistantMessage as appropriate.
 */
const ChatMessages = forwardRef<HTMLDivElement, ChatMessagesProps>(
	function ChatMessages(
		{
			messages,
			isStreaming,
			user,
			onApproveAction,
			onRejectAction,
			scrollRef,
			endRef,
			onScroll,
			emptyStateMessage = "Start a new conversation or select a previous chat.",
			showScrollTop,
			showScrollBottom,
			onScrollTop,
			onScrollBottom,
		},
		_ref,
	) {
		return (
			<>
				<div
					className="chat-messages"
					ref={scrollRef}
					onScroll={onScroll}
					role="log"
					aria-label="Conversation"
				>
					{messages.length === 0 && (
						<div className="chat-empty">
							<h2>Welcome to PIA</h2>
							<p>{emptyStateMessage}</p>
						</div>
					)}
					{messages.map((msg, index) => (
						<ChatMessage
							key={msg.id}
							message={msg}
							isStreaming={
								isStreaming &&
								msg.role === "assistant" &&
								index === messages.length - 1
							}
							user={user}
							onApproveAction={onApproveAction}
							onRejectAction={onRejectAction}
						/>
					))}
					<div ref={endRef} />
				</div>
				<ScrollButtons
					showScrollTop={showScrollTop}
					showScrollBottom={showScrollBottom}
					onScrollTop={onScrollTop}
					onScrollBottom={onScrollBottom}
				/>
			</>
		);
	},
);

export default ChatMessages;
